import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'

interface UsageStats {
  creditsUsed: number
  creditsLimit: number
  creditsRemaining: number
  percentageUsed: number
  isNearLimit: boolean // 70% or more
  isAtLimit: boolean // 90% or more
  isExceeded: boolean // 100% or more
  planType: string
}

export function useUsageControl() {
  const { user, profile } = useAuth()
  const [stats, setStats] = useState<UsageStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // Calculate usage statistics
  const calculateStats = useCallback((creditsUsed: number, creditsLimit: number, planType: string): UsageStats => {
    const creditsRemaining = Math.max(0, creditsLimit - creditsUsed)
    const percentageUsed = Math.round((creditsUsed / creditsLimit) * 100)

    return {
      creditsUsed,
      creditsLimit,
      creditsRemaining,
      percentageUsed,
      isNearLimit: percentageUsed >= 70,
      isAtLimit: percentageUsed >= 90,
      isExceeded: percentageUsed >= 100,
      planType
    }
  }, [])

  // Load usage stats
  const loadStats = useCallback(async () => {
    if (!user) {
      setStats(null)
      setLoading(false)
      return
    }

    try {
      setLoading(true)

      // Auto-reset if needed (checks if 30 days passed)
      await supabase.rpc('auto_reset_credits_if_needed', { user_id: user.id })

      // Get updated profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('credits_used, credits_limit, plan_type')
        .eq('id', user.id)
        .single()

      if (profileError) throw profileError

      const usageStats = calculateStats(
        profileData.credits_used || 0,
        profileData.credits_limit || 1000,
        profileData.plan_type || 'free'
      )

      setStats(usageStats)
      setError(null)
    } catch (err) {
      console.error('Error loading usage stats:', err)
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [user, calculateStats])

  // Check if user can perform action
  const canPerformAction = useCallback(async (creditsRequired: number = 1): Promise<{
    allowed: boolean
    reason?: string
    remainingCredits?: number
  }> => {
    if (!user) {
      return { allowed: false, reason: 'Not authenticated' }
    }

    try {
      // Check credits availability
      const { data, error } = await supabase.rpc('check_credits', {
        user_id: user.id,
        required_credits: creditsRequired
      })

      if (error) throw error

      if (!data) {
        const remaining = stats?.creditsRemaining || 0
        return {
          allowed: false,
          reason: `Insufficient credits. You need ${creditsRequired} credits but only have ${remaining} remaining.`,
          remainingCredits: remaining
        }
      }

      return {
        allowed: true,
        remainingCredits: stats?.creditsRemaining
      }
    } catch (err) {
      console.error('Error checking credits:', err)
      return {
        allowed: false,
        reason: 'Failed to check credit balance'
      }
    }
  }, [user, stats])

  // Use credits for an action
  const useCredits = useCallback(async (
    creditsToUse: number,
    action: string,
    toolId?: string,
    details?: Record<string, any>
  ): Promise<{ success: boolean; error?: string }> => {
    if (!user) {
      return { success: false, error: 'Not authenticated' }
    }

    // Check if action is allowed
    const checkResult = await canPerformAction(creditsToUse)
    if (!checkResult.allowed) {
      return { success: false, error: checkResult.reason }
    }

    try {
      // Increment credits used
      const { error: incrementError } = await supabase.rpc('increment_credits', {
        user_id: user.id,
        amount: creditsToUse
      })

      if (incrementError) throw incrementError

      // Log activity
      const { error: activityError } = await supabase
        .from('activities')
        .insert({
          user_id: user.id,
          tool_id: toolId,
          action,
          details,
          credits_used: creditsToUse
        })

      if (activityError) {
        console.error('Error logging activity:', activityError)
        // Don't fail the operation if activity logging fails
      }

      // Reload stats to reflect new usage
      await loadStats()

      return { success: true }
    } catch (err) {
      console.error('Error using credits:', err)
      return { success: false, error: (err as Error).message }
    }
  }, [user, canPerformAction, loadStats])

  // Get warning message based on usage
  const getUsageWarning = useCallback((): string | null => {
    if (!stats) return null

    if (stats.isExceeded) {
      return `You've exceeded your monthly limit of ${stats.creditsLimit} credits. Upgrade your plan to continue.`
    }

    if (stats.isAtLimit) {
      return `You're at ${stats.percentageUsed}% of your monthly limit (${stats.creditsRemaining} credits remaining).`
    }

    if (stats.isNearLimit) {
      return `You're at ${stats.percentageUsed}% of your monthly limit (${stats.creditsRemaining} credits remaining).`
    }

    return null
  }, [stats])

  // Load stats on mount and when profile changes
  useEffect(() => {
    loadStats()
  }, [loadStats])

  // Subscribe to profile changes
  useEffect(() => {
    if (!user) return

    const channel = supabase
      .channel('profile-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`
        },
        () => {
          loadStats()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, loadStats])

  return {
    stats,
    loading,
    error,
    canPerformAction,
    useCredits,
    getUsageWarning,
    refreshStats: loadStats
  }
}
